import { Route } from '@angular/router';
import { Path } from '../../../common/constants/paths.constant';
import { rfqDetailsResolver } from '../../../common/resolvers/rfq-details.resolver';
import { quotationDetailsResolver } from '../../../common/resolvers/quotation-details/quotation-details.resolver';
import { buyerRfqSinglePoResolver } from '../../../common/resolvers/po-details/po-details.resolver';
import { rfpDetailsResolver } from '../../../common/resolvers/rfp-details.resolver';
import { quotationRFPDetailsResolver } from '../../../common/resolvers/quotation-rfp-details.resolver';
import { inspectionCaseResolver } from '../../../common/resolvers/inspection-case/inspection-case.resolver';
import { rfpContractDetailsResolver } from '../../../common/resolvers/rfp-contract-details.resolver';

export const buyerRoutes: Route[] = [
  {
    path: '',
    redirectTo: `/${localStorage.getItem('lang') ?? 'ar'}/${Path.BUYER}/${Path.DASHBOARD}`,
    pathMatch: 'full',
  },
  {
    path: Path.DASHBOARD,
    loadComponent: () =>
      import('../../../pages/dashboard/buyer/dashboard/dashboard.component').then(
        c => c.DashboardComponent
      ),
  },
  {
    path: Path.PROJECTS,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/projects/projects.component').then(
            c => c.ProjectsComponent
          ),
      },
      {
        path: ':projectId',
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/projects/project-details/project-details.component').then(
            c => c.ProjectDetailsComponent
          ),
      },
    ],
  },
  {
    path: Path.RFQ.RFQ,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/rfq-board/rfq-board.component').then(
            c => c.RfqBoardComponent
          ),
      },
      {
        path: `${Path.RFQ.FORM}`,
        data: { role: 'buyer' },
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/rfq-form/rfq-form.component').then(
            c => c.RfqFormComponent
          ),
      },
      {
        path: `${Path.RFQ.FORM}/:rfqId`,
        data: { role: 'buyer' },
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/rfq-form/rfq-form.component').then(
            c => c.RfqFormComponent
          ),
        resolve: {
          rfqDetails: rfqDetailsResolver,
        },
      },
      {
        path: `${Path.RFQ.DETAILS}/:rfqId`,
        data: { role: 'buyer' },
        loadComponent: () =>
          import('../../../common/components/rfq-details-view/rfq-details-view.component').then(
            c => c.RfqDetailsViewComponent
          ),
        resolve: {
          rfqDetails: rfqDetailsResolver,
        },
      },
      {
        path: Path.RFQ.COMPARISON,
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/quotations/rfq/quotation-comparison/quotation-comparison.component').then(
            c => c.QuotationComparisonComponent
          ),
      },
      {
        path: `${Path.RFQ.SUPPLIER_DETAILS}/:supplierId`,
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/supplier-details/supplier-details.component').then(
            c => c.SupplierDetailsComponent
          ),
      },
    ],
  },

  {
    path: Path.RFP.RFP,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./rfp/rfp-board.component').then(c => c.RfpBoardComponent),
      },
      {
        path: `${Path.RFP.FORM}`,
        data: { role: 'buyer' },
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/rfp/rfp-form/rfp-form.component').then(
            c => c.RfpFormComponent
          ),
      },
      {
        path: `${Path.RFP.FORM}/:rfpId`,
        data: { role: 'buyer' },
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/rfp/rfp-form/rfp-form.component').then(
            c => c.RfpFormComponent
          ),
        resolve: {
          rfpDetails: rfpDetailsResolver,
        },
      },
      {
        path: `${Path.RFP.DETAILS}/:rfpId`,
        data: { role: 'buyer' },
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/rfp/rfp-details/rfp-details.component').then(
            c => c.RfpDetailsComponent
          ),
        resolve: {
          rfpDetails: rfpDetailsResolver,
        },
      },
      {
        path: Path.RFP.COMPARISON,
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/quotations/rfp/quotation-comparison/quotation-comparison.component').then(
            c => c.QuotationComparisonComponent
          ),
      },
      {
        path: `${Path.RFP.CONTRACTOR_DETAILS}/:contractorId`,
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/contractor-details/contractor-details.component').then(
            c => c.ContractorDetailsComponent
          ),
      },
    ],
  },

  {
    path: Path.QUOTATION.QUOTATION_RFQ,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./quotations/rfq/quotations.component').then(
            C => C.QuotationsComponent
          ),
      },
      {
        path: `${Path.QUOTATION.REVIEW}/:rfqId`,
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/quotations/rfq/quotation-review/quotation-review.component').then(
            c => c.QuotationReviewComponent
          ),
      },
      {
        path: `${Path.QUOTATION.DETAILS}/:rfqId/:quotationId`,
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/quotations/rfq/quotation-details-view/quotation-details-view.component').then(
            c => c.QuotationDetailsViewComponent
          ),
        resolve: { quotationDetails: quotationDetailsResolver },
      },
      // No `awarded` screen: awarding is bulk now, and its result is the RFQ's
      // purchase-order generation screen rather than a per-quotation success
      // page. The RFP flow still has one — it still awards one quotation.
    ],
  },
  {
    path: Path.QUOTATION.QUOTATION_RFP,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./quotations/rfp/quotations.component').then(
            C => C.QuotationsComponent
          ),
      },
      {
        path: `${Path.QUOTATION.DETAILS}/:rfpId/:quotationId`,
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/quotations/rfp/quotation-details-view/quotation-details-view.component').then(
            c => c.QuotationDetailsViewComponent
          ),
        resolve: { quotationRFPDetails: quotationRFPDetailsResolver },
      },
      // Awarding no longer produces a purchase order directly, so there is no
      // more per-quotation "awarded" success screen — award now creates an
      // RFP Contract and opens it straight away (see Path.CONTRACT).
    ],
  },

  {
    path: Path.PO.PO_RFQ,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/po/rfq/po.component').then(
            c => c.PoComponent
          ),
      },
      // `:id` is an RFQ id: one screen configures every purchase order the RFQ
      // generated, one section per group.
      {
        path: `${Path.PO.DETAILS}/:id`,
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/po/rfq/po-generation/po-generation.component').then(
            c => c.PoGenerationComponent
          ),
      },
      // One purchase order of that RFQ, for paying an installment and
      // confirming a delivery.
      {
        path: `${Path.PO.DETAILS}/:id/${Path.PO.PURCHASE_ORDER}/:poId`,
        loadComponent: () =>
          import('../../../pages/dashboard/buyer/po/rfq/po-details-view/po-details-view.component').then(
            c => c.PoDetailsViewComponent
          ),
        resolve: {
          poDetails: buyerRfqSinglePoResolver,
        },
      },
    ],
  },
  // The legacy RFP purchase-orders route ('po-rfp') and its screens/service/
  // resolver have been removed entirely — RFP Contracting (Path.CONTRACT)
  // replaces it.
  {
    path: Path.CONTRACT.CONTRACT,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./rfp-contract/rfp-contract-board.component').then(
            c => c.RfpContractBoardComponent
          ),
      },
      {
        path: `${Path.CONTRACT.DETAILS}/:contractId`,
        loadComponent: () =>
          import('./rfp-contract/rfp-contract-show/rfp-contract-show.component').then(
            c => c.RfpContractShowComponent
          ),
        resolve: {
          contractDetails: rfpContractDetailsResolver,
        },
      },
    ],
  },
  {
    path: Path.INSPECTION.CASES,
    data: { role: 'buyer' },
    loadComponent: () =>
      import('../../../common/pages/inspection-case-list/inspection-case-list.component').then(
        m => m.InspectionCaseListComponent
      ),
  },
  {
    path: `${Path.INSPECTION.CASES}/:caseId`,
    data: { role: 'buyer' },
    loadComponent: () =>
      import('../../../common/pages/inspection-case/inspection-case.component').then(
        m => m.InspectionCaseComponent
      ),
    resolve: {
      inspectionCase: inspectionCaseResolver,
    },
  },
  {
    path: Path.ACCOUNT,
    loadComponent: () =>
      import('../../../pages/dashboard/buyer/buyer-account/buyer-account.component').then(
        c => c.BuyerAccountComponent
      ),
  },
  {
    // Every not-yet-built service gets its own sub-path under coming-soon
    // (e.g. coming-soon/services/legal) so the sidenav highlights only the
    // clicked entry; they all render the same placeholder page.
    path: Path.SERVICE.COMING_SOON,
    children: [
      {
        path: '**',
        loadComponent: () =>
          import('../../../common/components/coming-soon/coming-soon.component').then(
            c => c.ComingSoonComponent
          ),
      },
    ],
  },
];
